import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingErrorsAtom,
  createErrorResult,
  errors,
  errorsCrudContentAtomFamily,
  hasKeys,
  IRideDto,
  Log,
  pendingRideFetchAtom,
  RideCrudModeKey,
  RideUpdateDriverOrVehiclePatch,
  RideUpdateModel,
  SimpleResult,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { ISaveRideParams } from "../hook-models";
import { useRideEditUtils } from "./use-ride-edit-utils";
import { useRidesActions } from "./use-rides-actions";

export const useRideEdit = () => {
  const setCrudServerErrors = useSetRecoilState(
    errorsCrudContentAtomFamily(RideCrudModeKey.uiUnit),
  );
  const {
    validateRideSaveForm,
    composeRideMetadata,
    processApiResponse,
    prepareRideForApi,
    handleRideUpdateOrCreate,
    invalidateDropdownItems,
  } = useRideEditUtils();
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setPendingRideFetch = useSetRecoilState(pendingRideFetchAtom);
  const { createRide, updateRideDriverOrVehicle } = useRidesActions();
  const { hasRideUpdateDriverPermission, hasRideUpdateVehiclePermission } =
    useHaveCustomPermissions();
  const saveRide = useEvent(
    async (
      params: ISaveRideParams,
    ): Promise<SimpleResult<RideUpdateModel | void>> => {
      const { rideDto, dryRun: dryRunSave, metadataDryRun } = params;
      setCrudServerErrors(undefined);
      setApiServerErrors(undefined);

      const isEdit = Boolean(rideDto.id);

      const { isFormValid, formValuesSnapshot } = await validateRideSaveForm(
        dryRunSave,
        isEdit,
      );

      if (!isFormValid) {
        return createErrorResult(errors.ride.formNotValidSkipDryCheckingRide);
      }

      const metadataComputed = composeRideMetadata(
        rideDto,
        metadataDryRun,
        formValuesSnapshot,
      );

      const rideForApiSend = prepareRideForApi(
        params,
        formValuesSnapshot,
        metadataComputed,
      );

      Log.debug("Ride composed data for api:", rideForApiSend);

      const rideApiResponse = await handleRideUpdateOrCreate({
        rideDto,
        rideForApiSend,
        dryRunSave,
        formValuesSnapshot,
      });

      invalidateDropdownItems(rideApiResponse);

      setPendingRideFetch(true);

      return processApiResponse(rideApiResponse, params, formValuesSnapshot);
    },
  );

  const saveRideDriverOrVehicle = useEvent(
    async ({
      rideDto,
    }: {
      rideDto: Partial<IRideDto>;
    }): Promise<SimpleResult<RideUpdateModel | void>> => {
      setCrudServerErrors(undefined);
      setApiServerErrors(undefined);

      const isEdit = Boolean(rideDto.id);

      const { isFormValid, formValuesSnapshot } = await validateRideSaveForm(
        false,
        isEdit,
      );

      if (!isFormValid) {
        return createErrorResult(errors.ride.formNotValidSkipDryCheckingRide);
      }

      const patchedFields: RideUpdateDriverOrVehiclePatch = {};

      if (
        hasRideUpdateDriverPermission &&
        formValuesSnapshot?.driverId !== rideDto.driverId
      ) {
        patchedFields.driverId = formValuesSnapshot?.driverId || null;
      }

      if (
        hasRideUpdateVehiclePermission &&
        formValuesSnapshot?.vehicleId !== rideDto.vehicleId
      ) {
        patchedFields.vehicleId = formValuesSnapshot?.vehicleId || null;
      }

      if (!hasKeys(patchedFields)) {
        return createErrorResult(
          errors.ride.noChangesToSave,
          "No fields changes detected ",
        );
      }

      Log.debug(
        "Ride composed data for api (updating driver or vehicle):",
        patchedFields,
      );

      const rideApiResponse = await updateRideDriverOrVehicle({
        rideId: rideDto?.id as number,
        isReturnDtoForGrid: false,
        patchedFields,
      });

      invalidateDropdownItems(rideApiResponse);

      setPendingRideFetch(true);

      const params: ISaveRideParams = { dryRun: false, rideDto };
      return processApiResponse(rideApiResponse, params, formValuesSnapshot);
    },
  );

  const createRideBySnapshot = useEvent(
    async (
      rideForApiSend: Partial<IRideDto>,
    ): Promise<SimpleResult<Partial<IRideDto>>> => {
      return createRide(rideForApiSend);
    },
  );

  return {
    saveRide,
    saveRideDriverOrVehicle,
    createRideBySnapshot,
  };
};
